<script lang="ts">
  import { onMount } from 'svelte'
  import { link } from 'svelte-spa-router'
  import { tStore } from '../i18n'
  import {
    highRelevanceNotifications,
    groupedNotifications,
    unreadCount,
    notificationsLoading,
    notificationsError,
    loadNotifications,
    markAllRead,
    dismiss,
    dismissGroup,
  } from '../stores/inAppNotifications'
  import { formatDateTime } from '../composables/format'

  const POLL_INTERVAL_MS = 15_000

  let open = $state(false)
  let buttonEl: HTMLButtonElement | undefined = $state()
  let panelEl: HTMLDivElement | undefined = $state()
  let expandedSources = $state<Set<string>>(new Set())

  function toggle(): void {
    open = !open
    if (open) void loadNotifications()
  }

  function close(): void {
    open = false
  }

  function toggleSource(sourceName: string): void {
    const next = new Set(expandedSources)
    if (next.has(sourceName)) next.delete(sourceName)
    else next.add(sourceName)
    expandedSources = next
  }

  function tenderCountLabel(count: number): string {
    const key = count === 1 ? 'bell.tenderSingular' : 'bell.tenderPlural'
    return `${count} ${$tStore(key)}`
  }

  function handleDocumentMouseDown(event: MouseEvent): void {
    if (!open) return
    const target = event.target as Node | null
    if (!target) return
    if (panelEl?.contains(target) || buttonEl?.contains(target)) return
    close()
  }

  function handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') close()
  }

  $effect(() => {
    if (typeof document === 'undefined') return
    document.addEventListener('mousedown', handleDocumentMouseDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
      document.removeEventListener('keydown', handleKey)
    }
  })

  onMount(() => {
    void loadNotifications()
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadNotifications()
    }, POLL_INTERVAL_MS)

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void loadNotifications()
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  })
</script>

<div class="bell">
  <button
    bind:this={buttonEl}
    type="button"
    class="bell__button"
    aria-label={$tStore('bell.label')}
    aria-haspopup="true"
    aria-expanded={open}
    onclick={toggle}
  >
    <svg
      class="bell__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 4.5 2 6 2 6H4s2-1.5 2-6Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
    {#if $unreadCount > 0}
      <span
        class="bell__badge"
        class:bell__badge--high={$highRelevanceNotifications.length > 0}
        aria-hidden="true"
      >
        {$unreadCount > 99 ? '99+' : $unreadCount}
      </span>
      <span class="visually-hidden">
        {$tStore('bell.unreadCount').replace('{count}', String($unreadCount))}
      </span>
    {/if}
  </button>

  {#if open}
    <div bind:this={panelEl} class="bell__panel" role="dialog" aria-label={$tStore('bell.label')}>
      <header class="bell__panel-header">
        <span class="bell__panel-title">{$tStore('bell.title')}</span>
        <button
          type="button"
          class="bell__link"
          onclick={markAllRead}
          disabled={$unreadCount === 0}
        >
          {$tStore('bell.markAllRead')}
        </button>
      </header>

      <div class="bell__panel-body">
        {#if $notificationsError}
          <p class="bell__empty error">{$notificationsError}</p>
        {:else if $unreadCount === 0}
          <p class="bell__empty muted">
            {$notificationsLoading ? $tStore('common.loading') : $tStore('bell.empty')}
          </p>
        {:else}
          {#if $highRelevanceNotifications.length > 0}
            <h3 class="bell__section-title bell__section-title--high">
              {$tStore('bell.highRelevance')}
            </h3>
            <ul class="bell__list">
              {#each $highRelevanceNotifications as n (n.id)}
                <li class="bell__item bell__item--unread bell__item--high">
                  <a
                    class="bell__item-link"
                    href={`/procurements/${n.procurementId}`}
                    use:link
                    onclick={() => {
                      dismiss(n.id)
                      close()
                    }}
                  >
                    <svg
                      class="bell__high-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 2.5c.5 3.1-1.4 4.7-3 6.3-1.4 1.5-2.5 3-2.5 5.3A5.5 5.5 0 0 0 12 19.5a5.5 5.5 0 0 0 5.5-5.5c0-3.2-1.8-5.8-5.5-11.5Zm.1 14.3a2.5 2.5 0 0 1-2.5-2.5c0-1.1.6-2 1.4-2.9.7-.7 1.4-1.5 1.5-2.7 1.4 2 2.1 3.6 2.1 5.4a2.5 2.5 0 0 1-2.5 2.7Z"
                      />
                    </svg>
                    <span class="bell__item-content">
                      <span class="bell__high-score">
                        {$tStore('bell.highRelevance')} · {n.aiScore}%
                      </span>
                      <span class="bell__item-title">{n.title}</span>
                    </span>
                  </a>
                  <span class="bell__item-actions">
                    <button
                      type="button"
                      class="bell__icon-btn"
                      aria-label={$tStore('bell.dismiss')}
                      title={$tStore('bell.dismiss')}
                      onclick={() => dismiss(n.id)}
                    >
                      ×
                    </button>
                  </span>
                </li>
              {/each}
            </ul>
          {/if}

          {#if $groupedNotifications.length > 0}
            <h3 class="bell__section-title">{$tStore('bell.regularUpdates')}</h3>
            <ul class="bell__list">
              {#each $groupedNotifications as group (group.sourceName)}
                <li class="bell__group">
                  <div class="bell__item bell__item--unread">
                    <button
                      type="button"
                      class="bell__item-link bell__group-toggle"
                      aria-expanded={expandedSources.has(group.sourceName)}
                      aria-label={$tStore(
                        expandedSources.has(group.sourceName)
                          ? 'bell.collapseSource'
                          : 'bell.expandSource',
                      ).replace('{source}', group.sourceName)}
                      onclick={() => toggleSource(group.sourceName)}
                    >
                      <span class="bell__item-dot" aria-hidden="true"></span>
                      <span class="bell__item-content">
                        <span class="bell__item-title">{group.sourceName}</span>
                        <span class="bell__item-meta">{tenderCountLabel(group.tenders.length)}</span
                        >
                      </span>
                      <span class="bell__chevron" aria-hidden="true">
                        {expandedSources.has(group.sourceName) ? '▴' : '▾'}
                      </span>
                    </button>
                    <span class="bell__item-actions">
                      <button
                        type="button"
                        class="bell__icon-btn"
                        aria-label={$tStore('bell.dismiss')}
                        title={$tStore('bell.dismiss')}
                        onclick={() => dismissGroup(group.tenders.map((tender) => tender.id))}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                  {#if expandedSources.has(group.sourceName)}
                    <ul class="bell__nested-list">
                      {#each group.tenders as tender (tender.id)}
                        <li>
                          <a
                            class="bell__nested-link"
                            href={`/procurements/${tender.procurementId}`}
                            use:link
                            onclick={() => {
                              dismiss(tender.id)
                              close()
                            }}
                          >
                            <span class="bell__item-title">{tender.title}</span>
                            <span class="bell__item-meta">
                              {formatDateTime(tender.publishedAt)}
                            </span>
                          </a>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
      </div>

      <footer class="bell__panel-footer">
        <button
          type="button"
          class="bell__link"
          onclick={() => void loadNotifications()}
          disabled={$notificationsLoading}
        >
          {$notificationsLoading ? $tStore('common.loading') : $tStore('common.refresh')}
        </button>
        <a href="/" use:link class="bell__link" onclick={close}>
          {$tStore('bell.viewAll')}
        </a>
      </footer>
    </div>
  {/if}
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .bell {
    position: relative;
    display: inline-flex;
  }

  .bell__button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: none;
    border: 1px solid transparent;
    border-radius: v.$radius-sm;
    color: v.$color-text-muted;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;

    &:hover,
    &[aria-expanded='true'] {
      background: v.$color-surface;
      border-color: v.$color-border;
      color: v.$color-text;
    }
  }

  .bell__icon {
    width: 20px;
    height: 20px;
  }

  .bell__badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: v.$color-danger-fg;
    color: #fff;
    border-radius: v.$radius-pill;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    box-shadow: 0 0 0 2px v.$color-surface-elevated;
  }

  .bell__badge--high {
    background: v.$color-primary;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .bell__panel {
    position: absolute;
    top: calc(100% + #{v.$space-xs});
    right: 0;
    width: min(380px, calc(100vw - #{v.$space-md} * 2));
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    box-shadow: v.$shadow-lg;
    z-index: 100;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: min(520px, calc(100vh - #{v.$header-height} - #{v.$space-md}));
  }

  .bell__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: v.$space-sm v.$space-md;
    border-bottom: 1px solid v.$color-border;
  }

  .bell__panel-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: v.$color-text;
  }

  .bell__panel-body {
    flex: 1;
    overflow-y: auto;
  }

  .bell__section-title {
    margin: 0;
    padding: v.$space-xs v.$space-md;
    border-bottom: 1px solid v.$color-border;
    background: v.$color-surface;
    color: v.$color-text-muted;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .bell__section-title--high {
    background: v.$color-warning-bg;
    color: v.$color-warning-fg;
  }

  .bell__panel-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: v.$space-sm v.$space-md;
    border-top: 1px solid v.$color-border;
    background: v.$color-surface;
  }

  .bell__link {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    color: v.$color-primary;
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      color: v.$color-text-subtle;
      cursor: default;
      text-decoration: none;
    }
  }

  .bell__empty {
    margin: 0;
    padding: v.$space-lg v.$space-md;
    text-align: center;
    font-size: 0.875rem;

    &.muted {
      color: v.$color-text-muted;
    }

    &.error {
      color: v.$color-danger-fg;
    }
  }

  .bell__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .bell__item {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid v.$color-border;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      @include v.interactive-hover($recolor-text: false);
    }
  }

  .bell__item--high {
    background: color-mix(in srgb, #{v.$color-warning-bg} 58%, white);
    border-left: 3px solid v.$color-warning-fg;

    &:hover {
      background: v.$color-warning-bg;
    }
  }

  .bell__item-link {
    flex: 1;
    display: flex;
    gap: v.$space-sm;
    align-items: flex-start;
    padding: v.$space-sm v.$space-md;
    color: inherit;
    text-decoration: none;
    min-width: 0;
  }

  .bell__group-toggle {
    width: 100%;
    border: 0;
    background: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .bell__high-icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    color: v.$color-warning-fg;
  }

  .bell__high-score {
    align-self: flex-start;
    padding: 1px 6px;
    border-radius: v.$radius-pill;
    background: v.$color-warning-bg;
    color: v.$color-warning-fg;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .bell__chevron {
    align-self: center;
    color: v.$color-text-muted;
    font-size: 0.75rem;
  }

  .bell__group {
    border-bottom: 1px solid v.$color-border;

    &:last-child {
      border-bottom: none;
    }

    > .bell__item {
      border-bottom: none;
    }
  }

  .bell__nested-list {
    list-style: none;
    margin: 0;
    padding: 0 0 v.$space-xs 2rem;
    background: v.$color-surface;

    li + li {
      border-top: 1px solid v.$color-border;
    }
  }

  .bell__nested-link {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: v.$space-sm v.$space-md;
    color: inherit;
    text-decoration: none;

    &:hover {
      background: v.$color-surface-elevated;
    }
  }

  .bell__item-dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    margin-top: 0.4rem;
    border-radius: 50%;
    background: transparent;
  }

  .bell__item--unread .bell__item-dot {
    background: v.$color-primary;
  }

  .bell__item-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .bell__item-title {
    font-size: 0.875rem;
    line-height: 1.35;
    color: v.$color-text;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .bell__item--unread .bell__item-title {
    font-weight: 600;
  }

  .bell__item-meta {
    font-size: 0.75rem;
    color: v.$color-text-muted;
  }

  .bell__item-actions {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    padding-right: v.$space-sm;
  }

  .bell__icon-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid transparent;
    background: none;
    border-radius: v.$radius-sm;
    color: v.$color-text-muted;
    font-size: 0.875rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: v.$color-surface-elevated;
      border-color: v.$color-border;
      color: v.$color-text;
    }
  }
</style>
